import { faPlus } from '@fortawesome/fontawesome-free-solid';
import classNames from 'classnames';
import { equals, map, pipe, toPairs } from 'ramda';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ACL, UserCan } from '../../../shared/constants';
import { UserAuth, iFullStoreState, iList, iPerson } from '../../../shared/interfaces';
import { history } from '../../../stores/store';
import DashboardBlock from '../../DashboardBlock';
import ModalHeader from '../../menus/modal/modal-header';
import { PersonTile } from './PersonTile';

import { clientDb } from '../../../shared/firebase';
import { localStorage } from "../../../shared/storage";
import { DashboardSize } from '../../../stores/reducers/dashboardInfo';
import styles from './PeoplePage.module.scss';
import { PersonListTile } from './PersonTile/PersonListTile';

enum ViewMode {
  GRID = 'grid',
  LIST = 'list',
}

export default function PeoplePage() {
  enum SortOptions {
    ASCBYLASTNAME = 'ascByLastName',
    DESCBYLASTNAME = 'descByLastName',
    ASCBYFIRSTNAME = 'ascByFirstName',
    DESCBYFIRSTNAME = 'descByFirstName',
  }
  const sortOptions = [
    { value: SortOptions.ASCBYLASTNAME, label: 'ASC by Last Name' },
    { value: SortOptions.DESCBYLASTNAME, label: 'DESC by Last Name' },
    { value: SortOptions.ASCBYFIRSTNAME, label: 'ASC by First Name' },
    { value: SortOptions.DESCBYFIRSTNAME, label: 'DESC by First Name' },
  ];
  const [sortOption, setSortOption] = useState(sortOptions[0]);
  const pageTitle = process.env.REACT_APP_PEOPLE_TAB_NAME || 'People';

  const handleSortChange = (selectedOption) => {
    setSortOption(selectedOption)
  };

  const people = useSelector<iFullStoreState, iList<iPerson>>(
    (s) => s.general.people,
    (l, r) => equals(l, r)
  );

  const loginToken = localStorage.get('login-init-user');


  const size = useSelector<iFullStoreState, DashboardSize>((s) => s.dashboardInfo.size);

  const dashboardNotBig = size !== DashboardSize.OPEN_FULL;

  const selectPeopleByIds = (state, ids) => {
    const people = state.general.people;
    const selectedPeople = {};

    if (!ids || !ids.length) {
      return selectedPeople;
    }

    for (const id of ids) {
      if (people[id]) {
        selectedPeople[id] = people[id];
      }
    }

    return selectedPeople;
  }

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

  const getFirstName = (name) => name?.trim().replace(/\s+/g, " ").split(" ")[0]?.toLowerCase() || "";
  const getLastName = (name) => {
    const parts = name?.trim().replace(/\s+/g, " ").split(" ") || [];
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0]?.toLowerCase() || "";
  };

  const compareByNameAscending = (a, b) => collator.compare(getFirstName(a.displayName), getFirstName(b.displayName));

  const compareByNameDescending = (a, b) => collator.compare(getFirstName(b.displayName), getFirstName(a.displayName));

  const compareByLastNameAscending = (a, b) => collator.compare(getLastName(a.displayName), getLastName(b.displayName));

  const compareByLastNameDescending = (a, b) => collator.compare(getLastName(b.displayName), getLastName(a.displayName));



  const getSortFunction = (sortOption) => {
    switch (sortOption.value) {
      case SortOptions.ASCBYLASTNAME:
        return compareByLastNameAscending;
      case SortOptions.DESCBYLASTNAME:
        return compareByLastNameDescending;
      case SortOptions.ASCBYFIRSTNAME:
        return compareByNameAscending;
      case SortOptions.DESCBYFIRSTNAME:
        return compareByNameDescending;
      default:
        return compareByNameDescending;
    }
  };

  const [isActiveStatus, setIsActiveStatus] = React.useState(true);

  const userCanDo = useSelector<iFullStoreState, Array<string>>(
    (s) => s.auth.user?.acl?.can
  );

  const userIsAdmin = ACL.check(UserCan.DO_ANYTHING, userCanDo);
  const userIsSubAdmin = ACL.check(UserCan.SUB_ADMIN, userCanDo);

  const authUser = useSelector<iFullStoreState, UserAuth>((s) => s.auth.user);

  const sourceArray: iPerson[] = useSelector(state => {
    if (userIsSubAdmin && !userIsAdmin) {
      const subUsersFromLoginToken = loginToken.subUsers || [];
      const subUsers = selectPeopleByIds(state, [...subUsersFromLoginToken, authUser.uid]);
      return Object.values(subUsers);
    } else {
      return Object.values(people);
    }
  });

  const sortedArray = sourceArray
    .sort(getSortFunction(sortOption))
    .map((el) => el);

  const [activePeople, disabledPeople] = React.useMemo(() => {
    const active = {} as iList<iPerson>;
    const disabled = {} as iList<iPerson>;

    Object.entries(sortedArray).forEach(([id, person]) => {
      if (person?.disabled) {
        disabled[id] = person;
      } else {
        active[id] = person;
      }
    });

    return [active, disabled]
  }, [sortedArray])

  useEffect(() => {
    if (!userIsAdmin && !userIsSubAdmin) {
      history.push(`person/${authUser.uid}`);
    }

    const fetchViewMode = async () => {
      const userFirebaseViewMode = (await clientDb().child(`people/people-details/${user.uid}/viewMode`).once('value')).val();
      if (userFirebaseViewMode) {
        setViewMode(userFirebaseViewMode);
      }
    };

    fetchViewMode();
  }, []);

  const moveToAddSubUserPage = useCallback(() => {
    history.push('/create-sub-user');
  }, []);

  const getHeaderAction = () => {
    if (!ACL.check(UserCan.DO_ANYTHING, userCanDo)) {
      return null;
    }

    return {
      fa: faPlus,
      click: moveToAddSubUserPage,
      title: 'Add sub user',
    };
  };

  const onSelectActiveStatus = useCallback(() => setIsActiveStatus(true), []);

  const onSelectDisabledStatus = useCallback(() => setIsActiveStatus(false), []);

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.GRID);

  const user = localStorage.get('login-init-user');

  const handleGridOrListView = async () => {
    const newViewMode = viewMode === ViewMode.GRID ? ViewMode.LIST : ViewMode.GRID;
    setViewMode(newViewMode);

    await clientDb().update({
      [`people/people-details/${user.uid}/viewMode`]: newViewMode,
    });
  };

  return (
    <DashboardBlock>
      <ModalHeader
        title={pageTitle}
        action={getHeaderAction()}
        showSelect={true}
        options={sortOptions}
        option={sortOption}
        handleChange={handleSortChange}
        dashboardSize={dashboardNotBig}
      />
      <div className={styles.peopleTypesSwitcher}>
        <div
          className={classNames({
            [styles.typeSelector]: true,
            [styles.activeType]: isActiveStatus
          })}
          onClick={onSelectActiveStatus}>
          Active
        </div>
        <div
          className={classNames({
            [styles.typeSelector]: true,
            [styles.activeType]: !isActiveStatus
          })}
          onClick={onSelectDisabledStatus}>
          Disabled
        </div>
      </div>
      <div
        className={viewMode === ViewMode.GRID ? styles.peopleGridWrapper : styles.peopleListWrapper}
      >
        {viewMode === ViewMode.GRID ? (
          pipe(
            toPairs,
            map(([id, person]) => <PersonTile key={id} person={person} />)
          )(isActiveStatus ? activePeople : disabledPeople)
        ) : (
          pipe(
            toPairs,
            map(([id, person]) => <PersonListTile key={id} person={person} />)
          )(isActiveStatus ? activePeople : disabledPeople)
        )}
      </div>
    </DashboardBlock>
  );
}
